import React, { useState } from "react";
import CalculatorPage from "./CalculatorPage";

// Length and weight convert through a base unit (meter / kilogram).
// Temperature is special-cased because its scales don't share a zero point.
const CATEGORIES = {
  length: {
    label: "Length",
    units: {
      millimeter: { label: "Millimeters (mm)", factor: 0.001 },
      centimeter: { label: "Centimeters (cm)", factor: 0.01 },
      meter: { label: "Meters (m)", factor: 1 },
      kilometer: { label: "Kilometers (km)", factor: 1000 },
      inch: { label: "Inches (in)", factor: 0.0254 },
      foot: { label: "Feet (ft)", factor: 0.3048 },
      yard: { label: "Yards (yd)", factor: 0.9144 },
      mile: { label: "Miles (mi)", factor: 1609.344 },
    },
  },
  weight: {
    label: "Weight",
    units: {
      gram: { label: "Grams (g)", factor: 0.001 },
      kilogram: { label: "Kilograms (kg)", factor: 1 },
      tonne: { label: "Metric Tons (t)", factor: 1000 },
      ounce: { label: "Ounces (oz)", factor: 0.028349523125 },
      pound: { label: "Pounds (lb)", factor: 0.45359237 },
      stone: { label: "Stones (st)", factor: 6.35029318 },
    },
  },
  temperature: {
    label: "Temperature",
    units: {
      celsius: { label: "Celsius (°C)" },
      fahrenheit: { label: "Fahrenheit (°F)" },
      kelvin: { label: "Kelvin (K)" },
    },
  },
};

function convertTemperature(value, from, to) {
  let celsius;
  if (from === "celsius") celsius = value;
  else if (from === "fahrenheit") celsius = ((value - 32) * 5) / 9;
  else celsius = value - 273.15;

  if (to === "celsius") return celsius;
  if (to === "fahrenheit") return (celsius * 9) / 5 + 32;
  return celsius + 273.15;
}

function UnitConverter() {
  const [category, setCategory] = useState("length");
  const [fromUnit, setFromUnit] = useState("meter");
  const [toUnit, setToUnit] = useState("foot");
  const [value, setValue] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const units = CATEGORIES[category].units;

  const changeCategory = (e) => {
    const next = e.target.value;
    const keys = Object.keys(CATEGORIES[next].units);
    setCategory(next);
    setFromUnit(keys[0]);
    setToUnit(keys[1]);
    setResult(null);
    setError("");
  };

  const convert = () => {
    const v = parseFloat(value);
    if (isNaN(v)) {
      setError("Please enter a value to convert.");
      setResult(null);
      return;
    }

    let converted;
    if (category === "temperature") {
      converted = convertTemperature(v, fromUnit, toUnit);
    } else {
      converted = (v * units[fromUnit].factor) / units[toUnit].factor;
    }

    setError("");
    setResult({
      from: `${v.toLocaleString()} ${units[fromUnit].label}`,
      to: `${converted.toLocaleString(undefined, { maximumFractionDigits: 6 })} ${units[toUnit].label}`,
    });
  };

  return (
    <CalculatorPage
      title="Unit Converter"
      metaDescription="Free unit converter for length, weight, and temperature: meters to feet, kilograms to pounds, Celsius to Fahrenheit, and more."
      intro={
        <>
          Convert between common <strong>length, weight, and temperature</strong>{" "}
          units — metric and imperial.
        </>
      }
      seoContent={
        <>
          <h2>How unit conversion works</h2>
          <p>
            Length and weight conversions go through a base unit (the meter
            and the kilogram): the value is first converted to the base unit,
            then to the target unit. That's why the factors are exact — one
            inch is defined as exactly 0.0254 meters and one pound as exactly
            0.45359237 kilograms.
          </p>
          <h2>Temperature is different</h2>
          <p>
            Temperature scales don't share a zero point, so they can't be
            converted with a single multiplication. Celsius to Fahrenheit is
            °F = °C × 9/5 + 32, and Kelvin is simply Celsius shifted by
            273.15. Water freezes at 0 °C, 32 °F, and 273.15 K.
          </p>
          <h2>Frequently asked questions</h2>
          <h3>How many centimeters are in an inch?</h3>
          <p>
            Exactly 2.54. The inch is defined as 0.0254 meters, so conversions
            between inches and centimeters are exact rather than approximate.
          </p>
          <h3>How do I convert kilograms to pounds?</h3>
          <p>
            Multiply the kilograms by about 2.20462. One kilogram equals
            2.20462 pounds because a pound is defined as exactly 0.45359237
            kilograms.
          </p>
          <h3>What's the quick way to convert Celsius to Fahrenheit?</h3>
          <p>
            Multiply by 9/5 and add 32. A common shortcut for a rough figure is
            to double the Celsius value and add 30, though the calculator gives
            the exact result.
          </p>
        </>
      }
    >
      <div className="fields">
        <label className="field">
          <span>Category</span>
          <select value={category} onChange={changeCategory}>
            {Object.entries(CATEGORIES).map(([key, cat]) => (
              <option key={key} value={key}>{cat.label}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Value</span>
          <input
            type="number"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setResult(null);
              setError("");
            }}
            placeholder="e.g. 100"
          />
        </label>
        <label className="field">
          <span>From</span>
          <select
            value={fromUnit}
            onChange={(e) => {
              setFromUnit(e.target.value);
              setResult(null);
            }}
          >
            {Object.entries(units).map(([key, u]) => (
              <option key={key} value={key}>{u.label}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>To</span>
          <select
            value={toUnit}
            onChange={(e) => {
              setToUnit(e.target.value);
              setResult(null);
            }}
          >
            {Object.entries(units).map(([key, u]) => (
              <option key={key} value={key}>{u.label}</option>
            ))}
          </select>
        </label>
      </div>

      <button className="btn" onClick={convert}>Convert</button>

      {error && <div className="error">{error}</div>}

      {result && (
        <div className="result">
          {result.from} = <strong>{result.to}</strong>
        </div>
      )}
    </CalculatorPage>
  );
}

export default UnitConverter;
